import { Container } from 'pixi.js';
import { TILE_HALF, tileToScreen } from '../sim/projection';

/**
 * The **tile-pinned overlay base** (Chassis Slice H, PRD #534 D7 / ADR-0271): the
 * shared chassis under the transient ground-FX layers - the tap-to-walk feedback
 * ({@link ./autowalkOverlay.AutowalkOverlay}, ADR-0091) and the storm spawn
 * telegraph ({@link ./telegraphOverlay.TelegraphOverlay}, ADR-0152) - which
 * shipped as copies of each other. A future ground cue extends this instead of
 * copying either.
 *
 * What the base owns (the part the twins duplicated):
 *
 * - **The overlay contract**: a child of the renderer's camera-transformed
 *   `world` container (so a tile-pinned graphic pans/zooms with the tile under
 *   it), `eventMode: 'none'` (pure visual feedback - never intercept the pointer
 *   events that drive the game beneath), and a far-above-the-sprites
 *   {@link OVERLAY_Z} zIndex so cues always draw on top of the depth-ranked
 *   scene.
 * - **The tile projection**: {@link centre} resolves a tile's ground-diamond
 *   centre in world-local pixels via the shared 2:1 dimetric
 *   {@link tileToScreen}, through the **z-aware {@link landZ} seam** (terraced
 *   land, PRD #536): the renderer wires it on heighted maps so a marker sits ON
 *   the terrace. It defaults to flat ground (z 0) - which is also the telegraph's
 *   contract today: storm telegraphs are always ground-level (the message carries
 *   no z, ADR-0152), so the telegraph simply never wires the seam. The day a roof
 *   summon is telegraphed, the seam is already here.
 * - **The transient lifecycle**: self-dismissing animated graphics aged each
 *   frame from the render clock ({@link ageTransients}) and destroyed when their
 *   TTL runs out; a static refresh (`dtMs <= 0`, e.g. a headless test frame)
 *   holds steady by contract in every subclass's `update`.
 *
 * Subclasses own their drawing and per-frame animation (what a ping/sparkle
 * looks like); the base owns where things pin and when they die.
 */

/** A discrete tile coordinate (structurally the renderer's `Tile`). */
export interface Tile {
  x: number;
  y: number;
}

/** zIndex within the sortable `world`: far above the depth-ranked sprites, so cues draw on top. */
export const OVERLAY_Z = 1_000_000;

/** The four corners of a tile diamond, centred on the origin (a 2:1 dimetric cell). */
export const DIAMOND: number[] = [0, -TILE_HALF, TILE_HALF, 0, 0, TILE_HALF, -TILE_HALF, 0];

/** A self-dismissing animated graphic, aged and culled by {@link TilePinnedOverlay.ageTransients}. */
export interface Transient {
  /** The display object to age; destroyed (children included) when the TTL runs out. */
  gfx: Container;
  ageMs: number;
  ttlMs: number;
}

export abstract class TilePinnedOverlay<T extends Transient = Transient> extends Container {
  /**
   * The land elevation under a tile (terraced land, PRD #536 Slice C): wired by
   * the renderer on heighted maps so a tile-pinned cue sits ON the terrace, not
   * `z * Z_STEP` px below it. Defaults to flat ground - on a flat map every
   * position below is byte-identical to the pre-height overlay, and the storm
   * telegraph (always ground-level today, ADR-0152) deliberately leaves it
   * unwired.
   */
  landZ: (x: number, y: number) => number = () => 0;

  /** Live self-dismissing graphics, aged and culled each frame. */
  protected readonly transients: T[] = [];

  constructor() {
    super();
    // Pure visual feedback: never intercept pointer events, and always draw above
    // the depth-ranked world sprites (the camera transform still applies, so a
    // tile-pinned cue tracks its tile as the view pans/zooms).
    this.eventMode = 'none';
    this.zIndex = OVERLAY_Z;
  }

  /** The centre of tile `(x, y)`'s ground diamond in world-local pixels, at its landZ. */
  protected centre(tile: Tile): { x: number; y: number } {
    const s = tileToScreen(tile.x, tile.y, this.landZ(tile.x, tile.y));
    // tileToScreen returns the diamond's top corner; drop half a tile to its middle.
    return { x: s.x, y: s.y + TILE_HALF };
  }

  /** Adopt a fresh self-dismissing graphic (already added as a child by the caller). */
  protected addTransient(transient: T): void {
    this.transients.push(transient);
  }

  /**
   * Advance every transient by `dtMs`: age it, destroy + drop any that has lived
   * out its TTL, and let the subclass animate the survivors via `animate(t, k)`
   * (`k` = the 0..1 life fraction). Callers gate on `dtMs <= 0` themselves so a
   * static refresh holds steady.
   */
  protected ageTransients(dtMs: number, animate: (t: T, k: number) => void): void {
    for (let i = this.transients.length - 1; i >= 0; i--) {
      const t = this.transients[i]!;
      t.ageMs += dtMs;
      const k = t.ageMs / t.ttlMs;
      if (k >= 1) {
        t.gfx.destroy({ children: true });
        this.transients.splice(i, 1);
        continue;
      }
      animate(t, k);
    }
  }
}
